/**
 * Iter-67 — Live landing-page impression + funnel-event beacon.
 *
 * Fire-and-forget analytics helper mounted from `LandingLayout.jsx` on
 * every /lp/<slug> page. Uses `navigator.sendBeacon` when available (never
 * blocks unload; survives page navigation) and falls back to `fetch` with
 * `keepalive: true` for browsers that don't have it (Safari <13 mostly).
 *
 * Session model:
 *   - `sessionStorage['lp_session_id']` holds a UUID scoped to the tab.
 *   - First beacon of a fresh session sets `is_new_session: true` so the
 *     server can bump `unique_sessions` exactly once per tab-day.
 *
 * Never sends PII. Never awaits. Never throws.
 */
import { useEffect } from "react";

const SESSION_KEY = "lp_session_id";
const SESSION_NEW_KEY = "lp_session_new_seen";

function backendUrl() {
  return process.env.REACT_APP_BACKEND_URL || "";
}

function ensureSessionId() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (window.crypto && typeof window.crypto.randomUUID === "function")
        ? window.crypto.randomUUID()
        : legacyUuid();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function legacyUuid() {
  // RFC-4122 v4 without `crypto.randomUUID` — used only when the
  // secure context isn't available (e.g. Karma test bench).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function isNewSession() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return false;
    if (window.sessionStorage.getItem(SESSION_NEW_KEY)) return false;
    window.sessionStorage.setItem(SESSION_NEW_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

function readParam(name) {
  try {
    if (typeof window === "undefined" || !window.location) return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(name) || null;
  } catch {
    return null;
  }
}

const ATTR_KEY = "lf_attribution";
const CLICK_PARAMS = ["click_id", "clickid", "cid"];

function attributionFromUrl() {
  return {
    aff: readParam("aff"),
    mb: readParam("mb"),
    sub: readParam("sub") || readParam("sub_id"),
    click_id: CLICK_PARAMS.map(readParam).find(Boolean) || null,
  };
}

// Who sent this visitor (affiliate / media buyer link + their click ID), kept for the thank-you page.
export function getAttribution() {
  const fromUrl = attributionFromUrl();
  try {
    if (fromUrl.aff || fromUrl.mb || fromUrl.click_id) {
      window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    return JSON.parse(window.sessionStorage.getItem(ATTR_KEY) || "null") || fromUrl;
  } catch {
    return fromUrl;
  }
}

/**
 * Fire a single LP engagement beacon.
 *
 * @param {string} slug        Current landing-page slug (required).
 * @param {string} eventType   One of: page_view, cta_primary_click,
 *                             cta_phone_click, form_start,
 *                             form_submit_attempt.
 * @param {object} [opts]      { forceNewSession?: boolean } — mostly
 *                             for tests to override the sessionStorage flag.
 */
export function trackLpEvent(slug, eventType, opts = {}) {
  try {
    if (!slug || !eventType) return;
    const url = `${backendUrl()}/api/public/lp/event`;
    const attr = eventType === "page_view" ? getAttribution() : {};
    const body = JSON.stringify({
      slug,
      event_type: eventType,
      session_id: ensureSessionId(),
      is_new_session: eventType === "page_view"
        ? (opts.forceNewSession ?? isNewSession())
        : false,
      aff: readParam("aff"),
      b: opts.b || readParam("b"),
      ...(attr.click_id ? { click_id: attr.click_id, sub: attr.sub, mb: attr.mb } : {}),
    });
    if (typeof navigator !== "undefined"
        && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
      // sendBeacon returned false → payload too large or CORS refusal.
      // Fall through to fetch below.
    }
    if (typeof fetch === "function") {
      // keepalive keeps the request alive across page-unload.
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Beacons never throw — a dead analytics call must not break the LP.
  }
}

/**
 * React hook — fires `page_view` on mount and attaches a click-delegation
 * listener that translates `[data-lp-cta="primary" | "phone"]` clicks into
 * their respective beacon events.
 *
 * @param {string|null} slug  The current LP slug, or null when unknown.
 */
export function useLpEngagementBeacon(slug) {
  useEffect(() => {
    if (!slug) return () => {};
    trackLpEvent(slug, "page_view");
    const onClick = (evt) => {
      const el = evt.target && evt.target.closest
        ? evt.target.closest("[data-lp-cta]")
        : null;
      if (!el) return;
      const kind = (el.getAttribute("data-lp-cta") || "").toLowerCase();
      if (kind === "primary") trackLpEvent(slug, "cta_primary_click");
      else if (kind === "phone") trackLpEvent(slug, "cta_phone_click", { b: el.getAttribute("data-lp-buyer") });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [slug]);
}
